'use client';

// base
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';

// styles
import { Column, Row } from 'styles';

// components
import { BasicPassword, BasicSelect, BasicTextField, TextButton } from 'components';
import { ForgotPassword } from '../ForgotPassword';
import { ChangePassword } from '../ChagePassword';
import { SignInBackground } from './SignInBackground';

// constants
import { ciImage, commonImage } from 'constants/images';

// domains
import { ReqLogin, useLogin } from 'domains/Auth/modules';
import { useFetchNations } from 'domains/Setting';
import { PrivacyModal, TermsModal } from 'domains/Terms';

// services
import {
  COOKIE_ACCESS_TOKEN,
  cookieStorage,
  STORAGE_LOCAL_LOGIN_ID,
  STORAGE_LOCAL_LOGIN_ID_SAVE,
  STORAGE_LOCAL_LOGIN_MOBILE_ID,
  STORAGE_LOCAL_USER_ID,
  storageService
} from 'services';

// contexts
import { useAlert } from 'contexts';

// libraries
import { Checkbox, Divider, IconButton, InputAdornment, TextField } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

export const Login: React.FC = () => {
  const [loginRequest, setLoginRequest] = useState<ReqLogin>({
    mobile: '',
    password: '',
    nationId: '5b393101-42b1-437f-a9d8-379430a7cf79'
  });
  const [isPrivacyModal, setIsPrivacyModal] = useState<boolean>(false);
  const [isTermsModal, setIsTermsModal] = useState<boolean>(false);
  const [isForgotPassword, setIsForgotPassword] = useState<boolean>(false);
  const [isChangePassword, setIsChangePassword] = useState<boolean>(false);
  const [saveIdChecked, setSaveIdChecked] = useState<boolean>(false);

  const router = useRouter();

  const { mutateAsync: login } = useLogin();
  const { data: nations } = useFetchNations({ data: { mobile: true } });

  const { showAlert } = useAlert();

  const handleLogin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    login(
      { data: loginRequest },
      {
        onSuccess: (data) => {
          cookieStorage.setCookie(COOKIE_ACCESS_TOKEN, data.resultObject.accessToken);
          storageService.local.setItem(STORAGE_LOCAL_USER_ID, data.resultObject.userId);
          if (saveIdChecked) {
            storageService.local.setItem(STORAGE_LOCAL_LOGIN_ID, loginRequest.mobile);
            storageService.local.setItem(STORAGE_LOCAL_LOGIN_MOBILE_ID, loginRequest.nationId);
          } else {
            storageService.local.removeItem(STORAGE_LOCAL_LOGIN_ID);
            storageService.local.removeItem(STORAGE_LOCAL_LOGIN_MOBILE_ID);
          }

          console.log('Login successful:', data);

          if (data.resultObject.temporaryPasswdStatus) {
            setIsChangePassword(true);
            return;
          }

          router.push('/dashboard');
        },
        onError: (error) => {
          showAlert('Login failed. Please check your credentials.');
        }
      }
    );
  };

  useEffect(() => {
    const savedMobile = storageService.local.getItem(STORAGE_LOCAL_LOGIN_ID);
    const savedNationId = storageService.local.getItem(STORAGE_LOCAL_LOGIN_MOBILE_ID);
    const savedIdChecked = storageService.local.getItem(STORAGE_LOCAL_LOGIN_ID_SAVE) === 'Y';

    if (savedMobile) {
      setLoginRequest((prev) => ({
        ...prev,
        mobile: savedMobile,
        nationId: savedNationId || ''
      }));
      setSaveIdChecked(savedIdChecked);
    }
  }, []);

  return (
    <div style={{ height: '100vh', width: '100vw', position: 'relative' }}>
      <Row
        style={{
          justifyContent: 'center',
          alignItems: 'center',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 2
        }}
      >
        <form onSubmit={handleLogin}>
          <Column
            gap="20px"
            style={{
              height: '440px',
              width: '470px',
              justifyContent: 'center',
              background: 'linear-gradient(212.65deg, #022495 -13.89%, #3C5AE2 118.24%)',
              borderRadius: 18,
              boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
              padding: '24px  0px'
            }}
          >
            <Row style={{ justifyContent: 'center', alignItems: 'end' }}>
              <img style={{ width: '30%' }} src={ciImage.logo4s} alt="logo" />
              <img style={{ width: '35%' }} src={ciImage.logoSamsungWhite} alt="samsung" />
            </Row>

            <Column
              gap="30px"
              style={{ justifyContent: 'center', alignItems: 'center', padding: '25px 0px' }}
            >
              <Column>
                <Row style={{ justifyContent: 'center' }}>
                  <BasicSelect
                    style={{ backgroundColor: '#fff', width: '162px' }}
                    value={loginRequest.nationId}
                    placeholder="Select"
                    options={(nations || []).map((nation) => ({
                      label: (
                        <Row style={{ alignItems: 'center' }}>
                          <img src={nation.flagUri} />
                          <Row gap="0px">
                            <div>{nation.countryCode}</div>
                            <div>{nation.numberCode}</div>
                          </Row>
                        </Row>
                      ),
                      value: nation.nationId
                    }))}
                    onChange={(e) =>
                      setLoginRequest((prev) => ({ ...prev, nationId: e.target.value as string }))
                    }
                  />
                  <BasicTextField
                    style={{ width: '162px' }}
                    value={loginRequest.mobile}
                    placeholder="1012345678"
                    onChange={(e) =>
                      setLoginRequest((prev) => ({
                        ...prev,
                        mobile: e.target.value as string
                      }))
                    }
                  />
                </Row>

                <Row style={{ justifyContent: 'center' }}>
                  <BasicPassword
                    style={{ width: '340px' }}
                    placeholder="PASSWORD"
                    value={loginRequest.password}
                    onChange={(e) =>
                      setLoginRequest((prev) => ({ ...prev, password: e.target.value as string }))
                    }
                  />
                </Row>

                <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <Row style={{ alignItems: 'center', gap: '8px' }}>
                    <Checkbox
                      icon={<img src={commonImage.checkboxOff} />}
                      checkedIcon={<img src={commonImage.checkboxOn} />}
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '4px',
                        background: '#FFFFFF',
                        padding: '0px'
                      }}
                      sx={{
                        p: 0,
                        '& .MuiSvgIcon-root': {
                          fontSize: 28
                        }
                      }}
                      checked={saveIdChecked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSaveIdChecked(true);
                          storageService.local.setItem(STORAGE_LOCAL_LOGIN_ID_SAVE, 'Y');
                        } else {
                          setSaveIdChecked(false);
                          storageService.local.removeItem(STORAGE_LOCAL_LOGIN_ID_SAVE);
                        }
                      }}
                    />
                    <div style={{ fontSize: '14px', fontWeight: '400', color: '#fff' }}>
                      Save ID
                    </div>
                  </Row>
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: '400',
                      color: '#fff',
                      cursor: 'pointer'
                    }}
                    onClick={() => setIsForgotPassword(true)}
                  >
                    Forgot Password?
                  </div>
                </Row>
              </Column>

              <Row style={{ justifyContent: 'end' }}>
                <TextButton style={{ width: '100px' }} label={'Log in'} type="submit" />
              </Row>
            </Column>

            <Row style={{ justifyContent: 'center' }}>
              <div
                style={{ color: '#fff', cursor: 'pointer' }}
                onClick={() => setIsPrivacyModal(true)}
              >
                Privacy Statement
              </div>
              <Divider
                orientation="vertical"
                flexItem
                style={{ backgroundColor: '#fff', height: '16px', margin: '0 8px' }}
              />
              <div
                style={{ color: '#fff', cursor: 'pointer' }}
                onClick={() => setIsTermsModal(true)}
              >
                Terms of Use
              </div>
            </Row>

            <Row style={{ justifyContent: 'center', alignItems: 'end' }}>
              <div style={{ width: '59px', height: '38px' }}>
                <img src={ciImage.gsilLogoNormal} alt="samsung-logo" />
              </div>

              <div style={{ color: '#fff', marginBottom: '12px', fontSize: '12px' }}>
                COPYRIGHT © 2025 BY GSIL Co., Ltd. All rights reserved.
              </div>
            </Row>
          </Column>
        </form>
      </Row>

      <SignInBackground />

      {isPrivacyModal && (
        <PrivacyModal open={isPrivacyModal} onClose={() => setIsPrivacyModal(false)} />
      )}

      {isTermsModal && <TermsModal open={isTermsModal} onClose={() => setIsTermsModal(false)} />}

      {isForgotPassword && (
        <ForgotPassword open={isForgotPassword} onClose={() => setIsForgotPassword(false)} />
      )}

      {isChangePassword && (
        <ChangePassword
          loginRequest={loginRequest}
          open={isChangePassword}
          onClose={() => setIsChangePassword(false)}
        />
      )}
    </div>
  );
};
